/* iter104 — Weekly automatic unpaid reminder rules (admin). */
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Wallet, Plus, Trash, Play } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '../../lib/api';

const fmt = (v) => (Number(v) || 0).toLocaleString('fr-FR').replace(/\u202f|,/g, ' ');
const EMPTY = { scope: 'class', target: '', min_balance: '0', weekday: '0', hour: '9', channels: ['sms'], message: '', enabled: true };

const UnpaidAutoRulesCard = () => {
  const [rules, setRules] = useState([]);
  const [weekdays, setWeekdays] = useState([]);
  const [seg, setSeg] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState('');

  const load = () => api.get('/sms/unpaid-rules').then(r => { setRules(r.data.items); setWeekdays(r.data.weekdays); }).catch(() => {});
  useEffect(() => {
    load();
    api.get('/sms/segments').then(r => { setSeg(r.data); setForm(f => ({ ...f, message: r.data.unpaid_default_template })); }).catch(() => {});
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const targetLabel = (r) => r.scope === 'all' ? "Toute l'école" : r.scope === 'cycle' ? `Niveau ${r.target}` : `Classe ${r.target}`;

  const create = async () => {
    setBusy('create');
    try {
      await api.post('/sms/unpaid-rules', { ...form, min_balance: Number(form.min_balance) || 0, weekday: Number(form.weekday), hour: Number(form.hour) });
      toast.success('Relance automatique créée'); setShowForm(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); } finally { setBusy(''); }
  };
  const toggle = async (r) => {
    try { await api.put(`/sms/unpaid-rules/${r.id}`, { enabled: !r.enabled }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };
  const remove = async (r) => {
    try { await api.delete(`/sms/unpaid-rules/${r.id}`); toast.success('Règle supprimée'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };
  const runNow = async (r) => {
    setBusy(r.id);
    try {
      const { data } = await api.post(`/sms/unpaid-rules/${r.id}/run`);
      data.ok ? toast.success(`Relance exécutée : ${data.recipients} famille(s), ${data.sms_sent} SMS`) : toast.warning(data.error || 'Aucune famille ciblée');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); } finally { setBusy(''); }
  };

  return (
    <Card className="border border-[#E2E8F0] shadow-none" data-testid="unpaid-rules-card">
      <CardHeader className="pb-2">
        <CardTitle className="font-outfit text-base flex items-center justify-between gap-2">
          <span className="flex items-center gap-2"><Wallet size={18} weight="duotone" className="text-amber-600" /> Relances automatiques d&apos;impayés (hebdo)</span>
          <Button size="sm" variant="outline" onClick={() => setShowForm(v => !v)} data-testid="unpaid-rule-new"><Plus size={14} className="mr-1" /> Nouvelle règle</Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {showForm && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="unpaid-rule-form">
            <div className="space-y-1"><Label className="text-xs">Segment</Label>
              <Select value={form.scope} onValueChange={v => { set('scope', v); set('target', ''); }}>
                <SelectTrigger data-testid="unpaid-rule-scope"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="class">Une classe</SelectItem><SelectItem value="cycle">Un niveau</SelectItem><SelectItem value="all">Toute l&apos;école</SelectItem></SelectContent>
              </Select></div>
            {form.scope !== 'all' && (
              <div className="space-y-1"><Label className="text-xs">{form.scope === 'class' ? 'Classe' : 'Niveau'}</Label>
                <Select value={form.target} onValueChange={v => set('target', v)}>
                  <SelectTrigger data-testid="unpaid-rule-target"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {form.scope === 'cycle' ? (seg?.cycles || []).map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)
                      : (seg?.cycles || []).flatMap(c => c.classes.map(k => <SelectItem key={k.name} value={k.name}>{k.name}</SelectItem>))}
                  </SelectContent>
                </Select></div>
            )}
            <div className="space-y-1"><Label className="text-xs">Solde minimum (FCFA)</Label>
              <Input type="number" min="0" value={form.min_balance} onChange={e => set('min_balance', e.target.value)} data-testid="unpaid-rule-min" /></div>
            <div className="space-y-1"><Label className="text-xs">Jour</Label>
              <Select value={form.weekday} onValueChange={v => set('weekday', v)}>
                <SelectTrigger data-testid="unpaid-rule-weekday"><SelectValue /></SelectTrigger>
                <SelectContent>{weekdays.map((w, i) => <SelectItem key={i} value={String(i)}>{w}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1"><Label className="text-xs">Heure (UTC)</Label>
              <Input type="number" min="0" max="23" value={form.hour} onChange={e => set('hour', e.target.value)} data-testid="unpaid-rule-hour" /></div>
            <div className="space-y-1 sm:col-span-3"><Label className="text-xs">Message ({'{balance}'} = solde de la famille)</Label>
              <textarea rows={3} value={form.message} onChange={e => set('message', e.target.value.slice(0, 320))} data-testid="unpaid-rule-message"
                className="w-full rounded-md border border-[#E2E8F0] p-2 text-sm" /></div>
            <div className="sm:col-span-3 flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowForm(false)}>Annuler</Button>
              <Button size="sm" onClick={create} disabled={busy === 'create' || (form.scope !== 'all' && !form.target) || !form.message.trim()}
                data-testid="unpaid-rule-save" className="bg-amber-600 hover:bg-amber-700 text-white">Créer la règle</Button>
            </div>
          </div>
        )}
        {rules.length === 0 && !showForm && <div className="text-sm text-[#94A3B8]" data-testid="unpaid-rules-empty">Aucune relance automatique configurée.</div>}
        <div className="divide-y divide-[#E2E8F0]">
          {rules.map(r => (
            <div key={r.id} className="py-2 flex flex-wrap items-center gap-3 text-sm" data-testid={`unpaid-rule-${r.id}`}>
              <Switch checked={r.enabled} onCheckedChange={() => toggle(r)} data-testid={`unpaid-rule-toggle-${r.id}`} />
              <div className="flex-1 min-w-[200px]">
                <div className="font-medium">{targetLabel(r)} · {weekdays[r.weekday]} {String(r.hour).padStart(2, '0')}h UTC · solde ≥ {fmt(r.min_balance)} FCFA</div>
                <div className="text-xs text-[#64748B] truncate" title={r.message}>{r.message}</div>
                {r.last_result && (
                  <div className="text-[11px] text-[#94A3B8]">
                    Dernière exécution : {new Date(r.last_result.at).toLocaleString('fr-FR')} — {r.last_result.ok ? `${r.last_result.recipients} famille(s), ${r.last_result.sms_sent} SMS` : r.last_result.error}
                  </div>
                )}
              </div>
              <Button size="sm" variant="outline" onClick={() => runNow(r)} disabled={busy === r.id} data-testid={`unpaid-rule-run-${r.id}`}><Play size={12} className="mr-1" /> Lancer</Button>
              <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(r)} data-testid={`unpaid-rule-delete-${r.id}`}><Trash size={14} /></Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default UnpaidAutoRulesCard;
